import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  api, clearSession, getSessionSignal, hasStoredSession, invalidateSessionRequests, isTabSession,
  onSessionEnded, refreshSession, type InvitationAccept, type TokenResponse,
} from "@/lib/api";
import { accountScope, createSessionQueryClient, retireSessionQueryClient } from "@/lib/session-cache";
import { atLeast, type AppUser, type Role } from "@/lib/types";

interface AuthValue {
  user: AppUser | null;
  /** True until the initial refresh-and-load has settled. Gate the router on this. */
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  /** Accept an invitation: the account is made, then signed in as `login` signs one in. */
  acceptInvitation: (input: InvitationAccept) => Promise<void>;
  /** Spend an admin's sign-in link: this browser becomes the person it was made for, or with
   *  `tab`, only this tab does (ADR-040 in `platform`). */
  signInWithLink: (token: string, tab?: boolean) => Promise<void>;
  /** Spend a forgotten-password link: the password is set and this browser is signed in as its
   *  owner, the way accepting an invitation signs in (ADR-057 in `platform`). */
  completePasswordReset: (token: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  reload: () => Promise<void>;
  can: (minimum: Role) => boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

/**
 * Whether somebody has signed in during THIS page load, as opposed to the page having been opened
 * or reloaded with a session already on the refresh cookie.
 *
 * Module-level rather than state, because signing in replaces the query client and remounts
 * everything under the provider — a flag in React state would be thrown away by the very event it
 * records. A full page load starts a new module instance, which is exactly the distinction wanted:
 * reloading a signed-in console does not count as signing in.
 *
 * It exists for the phone's audio setup, which is offered at sign-in and must not reappear on every
 * refresh (`softphone/audio-setup.tsx`).
 */
let signedInHere = false;

export function signedInThisPageLoad(): boolean {
  return signedInHere;
}

/** Auth owns the data boundary: no query client or account-owned React state outlives it. */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState(() => ({
    user: null as AppUser | null, revision: 0, mountKey: 0, client: createSessionQueryClient(),
  }));
  const current = useRef(session);
  const [loading, setLoading] = useState(true);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const refreshSchedule = useRef<{ expiresIn: number; deadline: number } | undefined>(undefined);

  const scheduleRefresh = useCallback((expiresIn: number, deadline?: number) => {
    clearTimeout(refreshTimer.current);
    const delay = deadline === undefined
      ? Math.max(10, expiresIn - 60) * 1000
      : Math.max(0, deadline - Date.now());
    refreshSchedule.current = { expiresIn, deadline: deadline ?? Date.now() + delay };
    const revision = current.current.revision;
    const signal = getSessionSignal();
    refreshTimer.current = setTimeout(async () => {
      try {
        const ok = await refreshSession();
        if (signal.aborted || revision !== current.current.revision) return;
        if (ok) scheduleRefresh(expiresIn);
        else clearSession();
      } catch {
        // Cancellation is expected when a newer login replaces a pending rotation.
        if (!signal.aborted && revision === current.current.revision) clearSession();
      }
    }, delay);
  }, []);

  const replaceSession = useCallback((user: AppUser | null) => {
    // A scope reassignment retains credentials and their deadline, not the old callback's
    // signal/revision. Logout and new logins must discard the previous refresh schedule.
    const refresh = current.current.user && user ? refreshSchedule.current : undefined;
    clearTimeout(refreshTimer.current);
    refreshSchedule.current = undefined;
    retireSessionQueryClient(current.current.client);
    const next = {
      user, revision: current.current.revision + 1, client: createSessionQueryClient(),
      // Anonymous attempts still retire caches and async work, but the form owns its
      // pending/error state until authentication succeeds. Only identity boundaries remount.
      mountKey: current.current.mountKey + (current.current.user || user ? 1 : 0),
    };
    current.current = next;
    setSession(next);
    if (refresh) scheduleRefresh(refresh.expiresIn, refresh.deadline);
  }, [scheduleRefresh]);

  const endSession = useCallback(() => {
    // Whoever signs in next is signing in, even in this same page load.
    signedInHere = false;
    replaceSession(null);
    setLoading(false);
  }, [replaceSession]);

  const loadUser = useCallback(async () => {
    const revision = current.current.revision;
    const me = await api.get<AppUser>("/auth/me");
    if (revision !== current.current.revision) throw new DOMException("Session changed", "AbortError");
    if (accountScope(me) !== accountScope(current.current.user)) {
      // /auth/me can reveal a tenant, role or endpoint reassignment without a login.
      if (current.current.user) invalidateSessionRequests();
      replaceSession(me);
    } else {
      const next = { ...current.current, user: me };
      current.current = next;
      setSession(next);
    }
  }, [replaceSession]);

  useEffect(() => onSessionEnded(endSession), [endSession]);

  useEffect(() => {
    let cancelled = false;
    const revision = current.current.revision;
    const signal = getSessionSignal();
    void (async () => {
      try {
        // A tab opening a sign-in link does not first sign in as the browser's own account: the link
        // gives this tab a session of its own (`routes/sign-in-as.tsx`).
        if (!openingSignInLink() && hasStoredSession() && await refreshSession()) {
          if (cancelled || signal.aborted || revision !== current.current.revision) return;
          await loadUser();
        }
      } catch {
        if (!cancelled && !signal.aborted && revision === current.current.revision) clearSession();
      } finally {
        if (!cancelled && !signal.aborted) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loadUser]);

  useEffect(() => () => {
    clearTimeout(refreshTimer.current);
    retireSessionQueryClient(current.current.client);
  }, []);

  /** Adopt the session a sign-in starts. `start` clears the old session before its first await,
   *  as `api.login` does, so the signal read after calling it belongs to the new identity. */
  const signIn = useCallback(async (start: () => Promise<TokenResponse>) => {
    endSession();
    const pending = start();
    const signal = getSessionSignal();
    const revision = current.current.revision;
    try {
      const tokens = await pending;
      signal.throwIfAborted();
      if (revision !== current.current.revision) throw new DOMException("Session changed", "AbortError");
      await loadUser();
      signal.throwIfAborted();
      scheduleRefresh(tokens.expires_in);
      // Set last, and only here: this is the one funnel every credential exchange goes through —
      // a password, an invitation, a sign-in link, a password reset — and none of the other ways a
      // session appears (the boot refresh, a rotation) passes through it.
      signedInHere = true;
    } catch (error) {
      if (!signal.aborted) clearSession();
      throw error;
    }
  }, [endSession, loadUser, scheduleRefresh]);

  const login = useCallback(
    (email: string, password: string) => signIn(() => api.login(email, password)),
    [signIn],
  );

  const acceptInvitation = useCallback(
    (input: InvitationAccept) => signIn(() => api.acceptInvitation(input)),
    [signIn],
  );

  const signInWithLink = useCallback(
    (token: string, tab = false) => signIn(() => api.signInWithLink(token, tab)),
    [signIn],
  );

  const completePasswordReset = useCallback(
    (token: string, password: string) =>
      signIn(() => api.completePasswordReset(token, password)),
    [signIn],
  );

  const logout = useCallback(async () => {
    // Local isolation must not depend on a successful (or even settled) network logout.
    endSession();
    await api.logout();
  }, [endSession]);

  const { user } = session;
  const value = useMemo<AuthValue>(() => ({
    user, loading, login, acceptInvitation, signInWithLink, completePasswordReset, logout,
    reload: loadUser,
    can: (minimum: Role) => atLeast(user?.role, minimum),
  }), [
    user, loading, login, acceptInvitation, signInWithLink, completePasswordReset, logout, loadUser,
  ]);

  return (
    <AuthContext.Provider value={value}>
      <QueryClientProvider key={session.mountKey} client={session.client}>
        {children}
      </QueryClientProvider>
    </AuthContext.Provider>
  );
}

/** This page load is a sign-in link being opened, in a tab with no session of its own yet. */
function openingSignInLink(): boolean {
  if (typeof window === "undefined" || isTabSession()) return false;
  return window.location.pathname === "/sign-in-as" && window.location.hash.length > 1;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
